import React, { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Loader,
  AlertCircle,
  User,
  Calendar,
  Tag,
  Sparkles,
  XCircle,
  Clock,
  Mail,
  UserPlus,
  Send,
  MessageCircle,
  ChevronRight,
  ShieldCheck,
  Code,
  PenTool,
  Server,
  Database,
  ExternalLink,
  Link as LinkIcon,
  LayoutDashboard,
  Activity,
  Users,
  Fingerprint,
  Copy,
  Check,
  BookOpen,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { supabase, TABLES, fromDb, fromDbList } from '../api/db';
import { useAuth } from '../contexts/AuthContext';
import { Task, TaskerProfile, ConnectionRequest, TaskTimelineEntry, AIAnalysis } from '../types';
import { getTaskerProfileByUserId } from '../api/taskerProfiles';
import { getAIAnalysisService } from '../services/ai/aiAnalysisService';
import AIAnalysisDisplay from '../components/AIAnalysisDisplay';
import GuideFramework from '../components/GuideFramework';
import {
  sendConnectionRequest,
  acceptConnectionRequest,
  rejectConnectionRequest,
} from '../api/connections';
import { notifyConnectionRequest } from '../api/notifications';
import TaskChat from '../components/TaskChat';
import TaskTimeline from '../components/TaskTimeline';
import TaskDetailEditor from '../components/TaskDetailEditor';
import TaskWorkspace from '../components/TaskWorkspace';
import {
  subscribeTaskTimeline,
  addTaskTimelineComment,
  deleteTaskTimelineEntry,
  syncTaskParticipantsForTask,
} from '../api/taskTimeline';
import { TASK_UNIVERSES } from '../constants/taskCreation';
import { normalizeTaskRow } from '../utils/taskRecords';

const TaskHatchDetail: React.FC = () => {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [task, setTask] = useState<Task | null>(null);
  /** Loaded from `tasks` only (no matching Task Hatch doc) — marketplace / seed tasks */
  const [loadedFromTasksCollection, setLoadedFromTasksCollection] = useState(false);
  const [profile, setProfile] = useState<TaskerProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // VISITOR (Tasker) STATE
  const [myConnectionReq, setMyConnectionReq] = useState<ConnectionRequest | null>(null);
  const [connectionMsg, setConnectionMsg] = useState('');
  const [sendingReq, setSendingReq] = useState(false);
  const [sendErr, setSendErr] = useState('');

  // OWNER STATE
  const [ownerConnections, setOwnerConnections] = useState<ConnectionRequest[]>([]);
  const [taskerNames, setTaskerNames] = useState<Record<string, string>>({});
  const [activeChatReqId, setActiveChatReqId] = useState<string | null>(null);
  const [timelineEntries, setTimelineEntries] = useState<TaskTimelineEntry[]>([]);
  const [activeTab, setActiveTab] = useState<'overview' | 'workspace' | 'progress' | 'people' | 'guide'>('overview');
  const [copiedCode, setCopiedCode] = useState(false);

  // IMPLEMENTATION GUIDE STATE. Paid tasks arrive AI-prefilled (Worker); free
  // tasks start empty and the owner/collaborators fill the framework in here.
  const [guide, setGuide] = useState<AIAnalysis | null>(null);
  const [guideLoading, setGuideLoading] = useState(true);
  const [creatingGuide, setCreatingGuide] = useState(false);
  // Set when redirected back from paid checkout (?guide=generating) so the
  // "generating" state shows even in the brief window before the pending row lands.
  const awaitingPaidGuide =
    typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('guide') === 'generating';

  const isOwner = currentUser && task && task.userId === currentUser.uid;
  /** Match Firestore rules + list queries: visibility or dataSensitivity can make a task discoverable. */
  const taskAllowsVisitorCollaboration = (t: Task) =>
    t.visibility === 'public' || t.dataSensitivity === 'public';
  const canConnect =
    currentUser &&
    task &&
    taskAllowsVisitorCollaboration(task) &&
    task.userId !== currentUser.uid;

  const canUserEditTask = isOwner || (myConnectionReq?.status === 'accepted');
  const canAccessTimeline =
    !!currentUser && !!task && (Boolean(isOwner) || myConnectionReq?.status === 'accepted');

  const dashboardTabs = useMemo(() => {
    const t: { id: 'overview' | 'workspace' | 'progress' | 'people' | 'guide'; label: string; icon: LucideIcon }[] = [
      { id: 'overview', label: 'Overview', icon: LayoutDashboard },
      // Visible on every task, paid or free — the empty state inside the tab
      // is what communicates "no guide," not the tab's presence.
      { id: 'guide', label: 'Guide', icon: BookOpen },
    ];
    if (canUserEditTask) t.push({ id: 'workspace', label: 'Workspace', icon: PenTool });
    if (canAccessTimeline) t.push({ id: 'progress', label: 'Progress', icon: Activity });
    t.push({ id: 'people', label: 'People & chat', icon: Users });
    return t;
  }, [canUserEditTask, canAccessTimeline]);

  useEffect(() => {
    if (!taskId || !isOwner || !currentUser) return;
    syncTaskParticipantsForTask(taskId, currentUser.uid).catch(() => {});
  }, [taskId, isOwner, currentUser]);

  useEffect(() => {
    if (!taskId || !task || !currentUser) return;
    const allowed = Boolean(isOwner) || myConnectionReq?.status === 'accepted';
    if (!allowed) {
      setTimelineEntries([]);
      return;
    }
    const unsub = subscribeTaskTimeline(
      taskId,
      setTimelineEntries,
      (e) => console.error('Timeline subscription:', e)
    );
    return () => unsub();
  }, [taskId, task, currentUser, isOwner, myConnectionReq?.status]);

  useEffect(() => {
    if (activeTab === 'workspace' && !canUserEditTask) setActiveTab('overview');
    if (activeTab === 'progress' && !canAccessTimeline) setActiveTab('overview');
  }, [activeTab, canUserEditTask, canAccessTimeline]);

  useEffect(() => {
    if (!task?.id) return;
    let cancelled = false;

    const loadGuide = async () => {
      const g = await getAIAnalysisService().getAnalysis(task.id);
      if (!cancelled) {
        setGuide(g);
        setGuideLoading(false);
      }
    };

    void loadGuide();
    const ch = supabase
      .channel(`task-guide-${task.id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: TABLES.AI_ANALYSES, filter: `task_id=eq.${task.id}` },
        () => void loadGuide()
      )
      .subscribe();

    return () => {
      cancelled = true;
      void supabase.removeChannel(ch);
    };
  }, [task?.id]);

  const startGuide = async () => {
    if (!task?.id) return;
    setCreatingGuide(true);
    const g = await getAIAnalysisService().createEmptyGuide(task.id);
    setCreatingGuide(false);
    if (g) setGuide(g);
  };

  useEffect(() => {
    if (!taskId || !currentUser) return;

    let unsubRequests: (() => void) | undefined;
    let cancelled = false;

    const loadTask = async () => {
      setLoading(true);
      setError('');
      try {
        let taskRow: Record<string, unknown> | null = null;
        let fromTasksOnly = false;

        try {
          const { data: hatchRow } = await supabase.from(TABLES.TASK_HATCH_SUBMISSIONS).select('*').eq('id', taskId).maybeSingle();
          taskRow = hatchRow as Record<string, unknown> | null;
        } catch { /* permission denied — try tasks table */ }

        if (!taskRow) {
          try {
            const { data: mktRow } = await supabase.from(TABLES.TASKS).select('*').eq('id', taskId).maybeSingle();
            taskRow = mktRow as Record<string, unknown> | null;
            fromTasksOnly = true;
          } catch {
            setError('Missing or insufficient permissions.');
            setTask(null);
            setLoading(false);
            return;
          }
        }

        if (cancelled) return;
        setLoadedFromTasksCollection(fromTasksOnly);
        if (!taskRow) {
          setError('Task not found.');
          setTask(null);
          setLoading(false);
          return;
        }
        const t = normalizeTaskRow(fromDb<Record<string, unknown>>(taskRow)) as Task;
        setTask(t);
        // A missing/failed owner profile must never break the task page (e.g.
        // 3rd-Eye-created tasks whose owner has no tasker_profiles row yet).
        try {
          const p = await getTaskerProfileByUserId(t.userId);
          if (cancelled) return;
          setProfile(p);
        } catch (profErr) {
          console.warn('Owner profile lookup failed (non-fatal):', profErr);
        }

        // Setup real-time listener for connections
        if (t.userId === currentUser.uid) {
          const loadOwnerConns = async () => {
            if (cancelled) return;
            const { data: rows } = await supabase
              .from(TABLES.CONNECTION_REQUESTS)
              .select('*')
              .eq('task_id', taskId)
              .eq('task_owner_id', currentUser.uid)
              .order('created_at', { ascending: false });
            if (cancelled) return;
            const reqs = fromDbList<ConnectionRequest>(rows ?? []);
            setOwnerConnections(reqs);

            const missingIds = Array.from(new Set(reqs.map((r) => r.taskerId)));
            const additions: Record<string, string> = {};
            for (const id of missingIds) {
              if (cancelled) break;
              try {
                const prof = await getTaskerProfileByUserId(id);
                const fromProfile = prof?.personalInfo?.displayName?.trim() ||
                  [prof?.personalInfo?.firstName, prof?.personalInfo?.lastName].filter(Boolean).join(' ').trim();
                if (fromProfile) { additions[id] = fromProfile; continue; }
                const { data: uRow } = await supabase.from(TABLES.USERS).select('display_name, email').eq('id', id).maybeSingle();
                const ud = uRow as { display_name?: string; email?: string } | null;
                additions[id] = ud?.display_name?.trim() || ud?.email?.split('@')[0] || 'Collaborator';
              } catch { additions[id] = 'Collaborator'; }
            }
            if (!cancelled) setTaskerNames((p) => ({ ...p, ...additions }));
          };

          if (cancelled) return;
          void loadOwnerConns();
          const ch = supabase.channel(`task-detail-conns-${taskId}`)
            .on('postgres_changes', { event: '*', schema: 'public', table: TABLES.CONNECTION_REQUESTS, filter: `task_id=eq.${taskId}` }, () => void loadOwnerConns())
            .subscribe();
          unsubRequests = () => { void supabase.removeChannel(ch); };
        } else {
          const loadMyConn = async () => {
            if (cancelled) return;
            const { data: rows } = await supabase
              .from(TABLES.CONNECTION_REQUESTS)
              .select('*')
              .eq('task_id', taskId)
              .eq('tasker_id', currentUser.uid)
              .order('created_at', { ascending: false });
            if (cancelled) return;
            const reqs = fromDbList<ConnectionRequest>(rows ?? []);
            reqs.length > 0 ? setMyConnectionReq(reqs[0]) : setMyConnectionReq(null);
          };

          if (cancelled) return;
          void loadMyConn();
          const ch = supabase.channel(`task-detail-myconn-${taskId}-${currentUser.uid}`)
            .on('postgres_changes', { event: '*', schema: 'public', table: TABLES.CONNECTION_REQUESTS, filter: `task_id=eq.${taskId}` }, () => void loadMyConn())
            .subscribe();
          unsubRequests = () => { void supabase.removeChannel(ch); };
        }
      } catch (e) {
        if (!cancelled) {
          console.error('loadTask failed:', e);
          setError(e instanceof Error ? e.message : 'Failed to load task.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadTask();
    return () => {
      cancelled = true;
      if (unsubRequests) unsubRequests();
    };
  }, [taskId, currentUser]);

  const handleSendConnection = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser || !taskId || !task) return;
    setSendingReq(true);
    setSendErr('');
    try {
      const requestId = await sendConnectionRequest(
        taskId,
        currentUser.uid,
        task.userId,
        connectionMsg.trim()
      );

      let taskerName = currentUser.displayName || currentUser.email || 'A tasker';
      try {
        const profile = await getTaskerProfileByUserId(currentUser.uid);
        if (profile?.personalInfo?.displayName) {
          taskerName = profile.personalInfo.displayName;
        }
      } catch {
        /* use default */
      }

      try {
        await notifyConnectionRequest(
          task.userId,
          currentUser.uid,
          task.id,
          task.title,
          taskerName,
          requestId
        );
      } catch (notifErr) {
        console.error('Error sending connection notification:', notifErr);
      }

      setConnectionMsg('');
    } catch (err) {
      setSendErr(err instanceof Error ? err.message : 'Could not send request.');
    } finally {
      setSendingReq(false);
    }
  };

  const formatDate = (ts: string | null | undefined) => {
    if (!ts) return '—';
    const d = new Date(ts);
    return isNaN(d.getTime()) ? '—' : d.toLocaleString();
  };

  const workspaceChatPeers = useMemo(() => {
    if (!currentUser || !task) return [];
    const creatorNameResolved =
      profile?.personalInfo?.displayName ||
      [profile?.personalInfo?.firstName, profile?.personalInfo?.lastName].filter(Boolean).join(' ') ||
      task.userName ||
      'Creator';
    const peers: { connectionId: string; otherUserId: string; otherName: string }[] = [];
    const userIsOwner = task.userId === currentUser.uid;
    if (userIsOwner) {
      ownerConnections
        .filter((c) => c.status === 'accepted')
        .forEach((c) => {
          peers.push({
            connectionId: c.id,
            otherUserId: c.taskerId,
            otherName: taskerNames[c.taskerId] || 'Collaborator',
          });
        });
    } else if (myConnectionReq?.status === 'accepted') {
      peers.push({
        connectionId: myConnectionReq.id,
        otherUserId: task.userId,
        otherName: creatorNameResolved,
      });
    }
    return peers;
  }, [currentUser, task, profile, ownerConnections, myConnectionReq, taskerNames]);

  if (!currentUser) {
    return (
      <div className="min-h-screen bg-tech-dark text-tech-text flex items-center justify-center px-4">
        <div className="text-center">
          <AlertCircle className="w-14 h-14 text-tech-cyan mx-auto mb-3" />
          <p className="text-tech-text-muted mb-4">Sign in to view this task.</p>
          <Link to="/login" className="text-tech-cyan hover:underline">
            Login
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-tech-dark flex items-center justify-center">
        <Loader className="w-10 h-10 text-tech-cyan animate-spin" />
      </div>
    );
  }

  if (error || !task) {
    return (
      <div className="min-h-screen bg-tech-dark text-tech-text px-4 py-12">
        <div className="max-w-2xl mx-auto tech-card rounded-xl p-8 text-center">
          <AlertCircle className="w-12 h-12 text-amber-400 mx-auto mb-3" />
          <p className="text-tech-text-muted">{error || 'Task unavailable.'}</p>
          <button
            type="button"
            onClick={() => navigate('/task-dashboard')}
            className="mt-6 px-4 py-2 rounded-lg border border-tech-cyan text-tech-cyan hover:bg-tech-cyan/10"
          >
            Back to dashboard
          </button>
        </div>
      </div>
    );
  }

  const creatorName =
    profile?.personalInfo?.displayName ||
    [profile?.personalInfo?.firstName, profile?.personalInfo?.lastName].filter(Boolean).join(' ') ||
    task.userName ||
    'Creator';

  const getTemplateBadge = (category: string) => {
    switch (category) {
      case 'code':
        return { icon: Code, label: 'Software & Code', color: 'text-tech-cyan', bg: 'bg-tech-cyan/10', border: 'border-tech-cyan/30' };
      case 'graphic':
        return { icon: PenTool, label: 'Digital & Graphic', color: 'text-pink-400', bg: 'bg-pink-500/10', border: 'border-pink-500/30' };
      case 'IT':
        return { icon: Server, label: 'IT & Network', color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30' };
      case 'database':
        return { icon: Database, label: 'Production / DBA', color: 'text-purple-400', bg: 'bg-purple-500/10', border: 'border-purple-500/30' };
      case 'security':
        return { icon: ShieldCheck, label: 'Security & Auth', color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/30' };
      default:
        return null;
    }
  };

  const badgeObj = task?.category ? getTemplateBadge(task.category) : null;

  const taskUniverseMeta = TASK_UNIVERSES.find((u) => u.id === task.universe);
  const taskTypeEmoji = taskUniverseMeta?.emoji ?? '✨';
  const taskTypeLabel = taskUniverseMeta?.label ?? null;

  return (
    <div className="min-h-screen bg-tech-dark text-tech-text bg-tech-mesh relative overflow-hidden flex flex-col">
      <div className="fixed top-0 right-0 w-[600px] h-[600px] bg-tech-cyan/5 rounded-full blur-[150px] pointer-events-none z-0" />

      <div className="max-w-[1600px] mx-auto relative z-10 w-full flex flex-col flex-1 min-h-0 px-4 py-6 md:py-8">
        <button
          type="button"
          onClick={() => navigate('/task-dashboard')}
          className="flex items-center gap-2 text-tech-text-muted hover:text-tech-cyan mb-4 transition-colors shrink-0"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Task hub
        </button>

        <div className="mb-4 flex flex-col gap-3 shrink-0">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3">
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase tracking-wider text-tech-cyan mb-1">Task dashboard</p>
              <h1 className="text-xl md:text-2xl font-bold animated-gradient-text pb-0.5 truncate">{task.title}</h1>
              {task.taskCode && (
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(task.taskCode!);
                    setCopiedCode(true);
                    setTimeout(() => setCopiedCode(false), 2000);
                  }}
                  className="mt-1 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gradient-to-r from-tech-cyan/10 to-blue-600/10 border border-tech-cyan/25 hover:border-tech-cyan/50 transition-all w-fit"
                  title="Click to copy Task ID — share it with anyone"
                >
                  <Fingerprint className="w-3.5 h-3.5 text-tech-cyan/70" />
                  <span className="font-mono text-xs font-bold tracking-widest text-tech-cyan">{task.taskCode}</span>
                  {copiedCode ? (
                    <span className="flex items-center gap-1 text-[10px] text-tech-accent"><Check className="w-3 h-3" /> Copied!</span>
                  ) : (
                    <Copy className="w-3 h-3 text-tech-text-muted/50" />
                  )}
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-2 shrink-0">
              <span className="px-3 py-1.5 rounded-lg text-xs border border-tech-border bg-tech-gray/40">{task.status}</span>
              <span className="px-3 py-1.5 rounded-lg text-xs border border-tech-cyan/30 text-tech-cyan">{task.priority}</span>
              {task.source === '3rdeyefeel' && (
                <span className="px-3 py-1.5 rounded-lg text-xs border border-violet-400/30 bg-violet-500/10 text-violet-200 font-medium">
                  ✦ Started on 3rd Eye Feel
                </span>
              )}
            </div>
          </div>
          <div className="flex gap-1 overflow-x-auto pb-1 border-b border-tech-border/50 -mx-1 px-1 hide-scrollbar">
            {dashboardTabs.map((tab) => {
              const Icon = tab.icon;
              const active = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-t-xl text-sm font-medium whitespace-nowrap transition-colors ${
                    active
                      ? 'bg-tech-cyan/15 text-tech-cyan border border-b-0 border-tech-cyan/35 shadow-[0_-4px_20px_rgba(34,211,238,0.08)]'
                      : 'text-tech-text-muted hover:text-tech-text border border-transparent'
                  }`}
                >
                  <Icon className="w-4 h-4 shrink-0" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Creator banner */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4 rounded-xl border border-tech-border/40 bg-tech-dark/30 px-4 py-3 shrink-0">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <span className="text-3xl leading-none select-none shrink-0" aria-hidden>{taskTypeEmoji}</span>
            <div className="min-w-0">
              <Link to={`/tasker/${task.userId}`} className="font-semibold text-tech-text hover:text-tech-cyan transition-colors truncate block">{creatorName}</Link>
              <p className="text-xs text-tech-text-muted truncate">
                {profile?.professionalInfo?.title || (taskTypeLabel ? `${taskTypeLabel} task` : 'Task creator')}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0 flex-wrap">
            <Link
              to={`/tasker/${task.userId}`}
              className="px-3 py-1.5 rounded-lg border border-tech-cyan/30 text-tech-cyan text-xs font-medium hover:bg-tech-cyan/10 transition-colors"
            >
              View profile
            </Link>
            <button
              type="button"
              onClick={() => navigate(`/tasks/${task.id}/get-email`)}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold text-tech-dark flex items-center gap-1.5"
              style={{ background: 'linear-gradient(135deg, #00d9ff 0%, #0066ff 100%)' }}
            >
              <Mail className="w-3.5 h-3.5" /> Get details via email
            </button>
          </div>
        </div>

        <div className="flex flex-col flex-1 min-h-0">
            <div className="flex-1 min-h-0 overflow-y-auto rounded-2xl border border-tech-border/40 bg-tech-dark/25 px-3 py-4 md:px-5 md:py-5 custom-scrollbar">
            {activeTab === 'overview' && (
            <div className={`glass-panel-heavy rounded-3xl p-6 md:p-8 border-tech-cyan/20 shadow-[0_0_40px_rgba(0,217,255,0.05)] ${canUserEditTask && !isOwner ? 'ring-1 ring-tech-cyan/50' : ''}`}>
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between mb-4">
                <div className="flex flex-wrap items-start gap-3 min-w-0 flex-1">
                  {badgeObj ? (
                    <div className={`p-3 rounded-2xl border ${badgeObj.bg} ${badgeObj.border} shadow-lg shrink-0`}>
                      <badgeObj.icon className={`w-8 h-8 ${badgeObj.color}`} />
                    </div>
                  ) : (
                    <Sparkles className="w-8 h-8 text-tech-cyan flex-shrink-0" />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-3 flex-wrap mb-1">
                      <h2 className="text-lg md:text-xl font-semibold text-tech-text">Brief</h2>
                      {badgeObj && (
                        <span className={`px-2 py-0.5 text-[10px] uppercase font-bold tracking-wider rounded-md border ${badgeObj.bg} ${badgeObj.border} ${badgeObj.color}`}>
                          {badgeObj.label} Template
                        </span>
                      )}
                    </div>
                    <p className="text-tech-text-muted leading-relaxed">{task.summary}</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => navigate(`/tasks/${task.id}/get-email`)}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-semibold border border-tech-cyan/50 text-tech-cyan bg-tech-cyan/10 hover:bg-tech-cyan/20 transition-colors"
                  >
                    <Mail className="w-4 h-4" />
                    Get email
                  </button>
                  {canUserEditTask && (
                    <button
                      type="button"
                      onClick={() =>
                        loadedFromTasksCollection
                          ? navigate(`/marketplace?task=${task.id}`)
                          : navigate(`/task-hatch?edit=${task.id}`)
                      }
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-tech-cyan/40 text-tech-text hover:bg-tech-cyan/10 transition-colors shadow-[0_0_15px_rgba(34,211,238,0.1)] focus:outline-none focus:ring-2 focus:ring-tech-cyan"
                    >
                      <PenTool className="w-4 h-4 text-tech-cyan" />
                      Task Hatch wizard
                    </button>
                  )}
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-3 text-sm text-tech-text-muted mb-6">
                <span className="flex items-center gap-2 min-w-0">
                  <User className="w-4 h-4 text-tech-cyan shrink-0" />
                  <Link
                    to={`/tasker/${task.userId}`}
                    className="text-tech-text hover:text-tech-cyan truncate transition-colors font-medium"
                  >
                    {task.userName}
                  </Link>
                </span>
                <span className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-tech-cyan" />
                  {formatDate(task.createdAt)}
                </span>
                {task.category && (
                  <span className="flex items-center gap-2">
                    <Tag className="w-4 h-4 text-tech-cyan" />
                    {task.category}
                  </span>
                )}
                <span
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs border ${
                    task.visibility === 'public'
                      ? 'border-tech-accent/50 text-tech-accent'
                      : 'border-tech-border text-tech-text-muted'
                  }`}
                >
                  {task.visibility === 'public' ? 'Public' : 'Private'}
                </span>
              </div>

              <div className="space-y-4 border-t border-tech-border pt-6">
                {!canUserEditTask ? (
                  <>
                    <h2 className="text-sm font-semibold uppercase tracking-wide text-tech-cyan">Requirements</h2>
                    <p className="text-sm text-tech-text-muted whitespace-pre-wrap">{task.requirements || '—'}</p>
                    <h2 className="text-sm font-semibold uppercase tracking-wide text-tech-cyan">Acceptance criteria</h2>
                    <p className="text-sm text-tech-text-muted whitespace-pre-wrap">{task.acceptanceCriteria || '—'}</p>
                  </>
                ) : (
                  <p className="text-sm text-tech-text-muted">
                    Edit the full brief, dates, and status in the <strong className="text-tech-text">Workspace</strong> tab.
                    Use <strong className="text-tech-text">Progress</strong> for the 3D chrono rail and activity log.
                  </p>
                )}

                {task.relatedLinks && task.relatedLinks.length > 0 && (
                  <>
                    <h2 className="text-sm font-semibold uppercase tracking-wide text-indigo-400 mt-6 pt-4 border-t border-tech-border/50 flex items-center gap-2">
                       <LinkIcon className="w-4 h-4" /> Attached Resources
                    </h2>
                    <div className="grid sm:grid-cols-2 gap-3 mt-2">
                      {task.relatedLinks.map((link, idx) => (
                        <a
                          key={idx}
                          href={link.url.startsWith('http') ? link.url : `https://${link.url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-between p-3 rounded-xl bg-white/5 border border-indigo-500/20 hover:border-indigo-400/50 hover:bg-white/10 transition-all group"
                        >
                          <div className="min-w-0 pr-2">
                            <h4 className="font-semibold text-tech-text text-sm truncate group-hover:text-indigo-300 transition-colors">{link.title}</h4>
                            <p className="text-xs text-tech-text-muted truncate mt-0.5">{link.url}</p>
                          </div>
                          <ExternalLink className="w-4 h-4 text-tech-text-muted group-hover:text-indigo-400 shrink-0" />
                        </a>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
            )}

            {activeTab === 'guide' && (
              <div>
                {guideLoading ? (
                  <div className="glass-panel-heavy rounded-3xl p-6 md:p-8 border-tech-cyan/20 py-12 text-center">
                    <Loader className="w-8 h-8 text-tech-cyan animate-spin mx-auto mb-3" />
                  </div>
                ) : (guide && guide.status === 'pending') || (!guide && awaitingPaidGuide) ? (
                  <div className="glass-panel-heavy rounded-3xl p-6 md:p-8 border-tech-cyan/20 py-12 text-center">
                    <div className="relative w-14 h-14 mx-auto mb-6">
                      <div className="absolute inset-0 rounded-full border-2 border-tech-cyan/20" />
                      <div className="absolute inset-0 rounded-full border-t-2 border-tech-cyan animate-spin" />
                    </div>
                    <p className="text-tech-text-muted">Claude is pre-filling your Implementation Guide…</p>
                    <p className="text-tech-text-muted/60 text-sm mt-2">All ten components — this usually takes 1-2 minutes. It appears here automatically.</p>
                  </div>
                ) : guide && guide.status === 'failed' ? (
                  <div className="glass-panel-heavy rounded-3xl p-6 md:p-8 border-tech-cyan/20 py-12 text-center">
                    <AlertCircle className="w-12 h-12 text-amber-400 mx-auto mb-3" />
                    <h3 className="text-lg font-semibold text-tech-text mb-1">Guide pre-fill failed</h3>
                    <p className="text-tech-text-muted text-sm max-w-sm mx-auto mb-4">
                      {guide.error || 'Something went wrong pre-filling this guide.'}
                    </p>
                    {canUserEditTask && (
                      <button onClick={startGuide} disabled={creatingGuide} className="border border-tech-cyan/50 text-tech-cyan bg-tech-cyan/10 hover:bg-tech-cyan/20 font-semibold transition-colors inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm">
                        {creatingGuide ? <Loader className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                        Start it yourself instead
                      </button>
                    )}
                  </div>
                ) : guide && guide.guideComponents && guide.guideComponents.length > 0 ? (
                  <GuideFramework taskId={task.id} analysis={guide} canEdit={!!canUserEditTask} />
                ) : guide ? (
                  // Legacy row (old 5-section shape) — render the classic display.
                  <div className="glass-panel-heavy rounded-3xl p-6 md:p-8 border-tech-cyan/20">
                    <AIAnalysisDisplay analysis={guide} />
                  </div>
                ) : canUserEditTask ? (
                  <div className="glass-panel-heavy rounded-3xl p-8 md:p-10 border-tech-cyan/20 text-center">
                    <div className="w-14 h-14 rounded-2xl bg-tech-cyan/10 flex items-center justify-center mx-auto mb-4">
                      <BookOpen className="w-7 h-7 text-tech-cyan" />
                    </div>
                    <h3 className="text-lg font-semibold text-tech-text mb-1">Build your Implementation Guide</h3>
                    <p className="text-tech-text-muted text-sm max-w-md mx-auto mb-5">
                      Ten components — resources, steps, navigation, research, prompts and more — to plan
                      and track this task. Start it here for free, or get it AI pre-filled from 3rd Eye Feel.
                    </p>
                    <button onClick={startGuide} disabled={creatingGuide} className="border border-tech-cyan/50 text-tech-cyan bg-tech-cyan/10 hover:bg-tech-cyan/20 font-semibold transition-colors inline-flex items-center gap-2 px-5 py-2.5 rounded-xl">
                      {creatingGuide ? <Loader className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                      Start my guide
                    </button>
                  </div>
                ) : (
                  <div className="glass-panel-heavy rounded-3xl p-6 md:p-8 border-tech-cyan/20 py-12 text-center">
                    <BookOpen className="w-12 h-12 text-tech-text-muted mx-auto mb-3" />
                    <h3 className="text-lg font-semibold text-tech-text mb-1">No guide yet</h3>
                    <p className="text-tech-text-muted text-sm max-w-sm mx-auto">
                      The owner hasn't built this task's implementation guide yet.
                    </p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'workspace' && canUserEditTask && (
              <div id="task-workspace" className="scroll-mt-24 space-y-6">
                <TaskWorkspace task={task} chatPeers={workspaceChatPeers} />
                {isOwner && (
                  <details className="glass-panel rounded-2xl border-tech-border/30 overflow-hidden">
                    <summary className="px-5 py-3 cursor-pointer text-sm font-semibold text-tech-text-muted hover:text-tech-text transition-colors flex items-center gap-2">
                      <PenTool className="w-4 h-4" /> Advanced Task Editor
                    </summary>
                    <div className="p-1">
                      <TaskDetailEditor
                        task={task}
                        collectionName={
                          loadedFromTasksCollection ? TABLES.TASKS : TABLES.TASK_HATCH_SUBMISSIONS
                        }
                        onUpdated={(next) => setTask(next)}
                        className="border-tech-border/50 shadow-inner"
                      />
                    </div>
                  </details>
                )}
              </div>
            )}

            {activeTab === 'progress' && canAccessTimeline && currentUser && (
              <TaskTimeline
                task={task}
                entries={timelineEntries}
                canComment
                currentUserId={currentUser.uid}
                onAddComment={async (body) => {
                  await addTaskTimelineComment(task.id, currentUser.uid, body);
                }}
                onDeleteComment={async (entryId) => {
                  await deleteTaskTimelineEntry(entryId);
                }}
                className="min-h-0"
              />
            )}

            {activeTab === 'people' && (
            <>
            {/* CONNECTION SECTION FOR VISITOR (If NOT Owner) */}
            {canConnect && (
              <div className="glass-panel rounded-3xl p-6 md:p-8 border-tech-cyan/20">
                {!myConnectionReq ? (
                  // State 1: Send Request
                  <>
                    <h2 className="text-xl font-bold text-tech-text mb-2 flex items-center gap-3">
                      <UserPlus className="w-6 h-6 text-tech-accent" />
                      Connect to Discuss Task
                    </h2>
                    <p className="text-sm text-tech-text-muted mb-6 mt-2">
                      Send a secure connection request to the task owner. Once approved, you can chat with them live directly here on the task page!
                    </p>
                    <form onSubmit={handleSendConnection} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-tech-text mb-1">Optional intro message</label>
                        <textarea
                          value={connectionMsg}
                          onChange={(e) => setConnectionMsg(e.target.value)}
                          rows={3}
                          className="w-full rounded-xl bg-tech-dark border border-tech-border px-4 py-3 text-tech-text placeholder-tech-text-muted/50 focus:outline-none focus:ring-2 focus:ring-tech-accent/50 transition-shadow"
                          placeholder="Hey, I've got experience in this stack and can help out..."
                        />
                      </div>
                      {sendErr && <p className="text-sm text-amber-300 bg-amber-500/10 p-2 rounded">{sendErr}</p>}
                      <button
                        type="submit"
                        disabled={sendingReq}
                        className="px-6 py-3 rounded-xl font-bold text-tech-dark disabled:opacity-50 flex items-center gap-2 hover:opacity-90 transition-opacity"
                        style={{ background: 'linear-gradient(135deg, #00d9ff 0%, #0066ff 100%)' }}
                      >
                        {sendingReq ? <Loader className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                        {sendingReq ? 'Sending Request...' : 'Send Connection Request'}
                      </button>
                    </form>
                  </>
                ) : myConnectionReq.status === 'pending' ? (
                  // State 2: Pending
                  <div className="text-center py-8">
                    <Clock className="w-16 h-16 text-amber-500/80 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-tech-text mb-2">Request Pending</h3>
                    <p className="text-tech-text-muted max-w-sm mx-auto">
                      Your connection request has been sent to the task owner. We'll notify you once they accept it.
                    </p>
                  </div>
                ) : myConnectionReq.status === 'rejected' ? (
                  // State 3: Rejected
                  <div className="text-center py-6">
                    <XCircle className="w-12 h-12 text-tech-text-muted mx-auto mb-3" />
                    <h3 className="text-lg font-medium text-tech-text mb-1">Connection Declined</h3>
                    <p className="text-tech-text-muted text-sm">
                      The task owner declined this request. You can focus on other opportunities in the marketplace!
                    </p>
                  </div>
                ) : (
                  // State 4: Accepted (Chat View)
                  <div className="flex flex-col h-[650px] md:h-[750px] mt-4">
                    <div className="flex items-center gap-3 mb-6">
                      <ShieldCheck className="w-7 h-7 text-tech-cyan" />
                      <div>
                        <h2 className="text-2xl font-bold text-tech-text uppercase tracking-wider">Active Discussion Board</h2>
                        <p className="text-xs text-tech-cyan/80 font-mono mt-1">E2E ENCRYPTED SESSION WITH {creatorName.toUpperCase()}</p>
                      </div>
                    </div>
                    {/* The TaskChat component renders fully equipped with input, scrollable history, attachment stubs */}
                    <div className="flex-1 min-h-[400px] rounded-2xl overflow-hidden shadow-[0_0_30px_rgba(0,217,255,0.15)] border-2 border-tech-cyan/30">
                      <TaskChat
                        connectionId={myConnectionReq.id}
                        taskId={task.id}
                        otherParticipantName={creatorName}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* OWNER CONNECTION SECTION */}
            {isOwner && (
              <div className="glass-panel rounded-3xl p-6 md:p-8 space-y-6 border-tech-cyan/20">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold text-tech-text flex items-center gap-3">
                    <UserPlus className="w-6 h-6 text-tech-cyan" />
                    Connection Requests
                  </h2>
                </div>
                
                <p className="text-sm text-tech-text-muted">
                  Users interested in collaborating securely on your task will appear here. Accept a request to open a dedicated task chat directly on this page!
                </p>

                {ownerConnections.length === 0 ? (
                  <div className="flex flex-col items-center justify-center p-8 bg-tech-gray/20 rounded-xl border border-tech-border border-dashed">
                    <MessageCircle className="w-10 h-10 text-tech-text-muted mb-2 text-opacity-50" />
                    <p className="text-tech-text-muted">No connection requests yet.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {ownerConnections.map((req) => (
                      <div key={req.id} className="rounded-xl border border-tech-border bg-tech-dark/50 overflow-hidden text-sm">
                        
                        {/* Header snippet: Always visible */}
                        <div className="p-4 md:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-tech-border/50 hover:bg-tech-gray/20 transition-colors">
                          <div className="flex items-start gap-4">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-tech-cyan to-blue-600 flex items-center justify-center text-white font-bold text-lg shrink-0 shadow-lg select-none">
                              {taskerNames[req.taskerId]?.charAt(0) || '?'}
                            </div>
                            <div>
                              <Link to={`/tasker/${req.taskerId}`} className="font-semibold text-tech-text text-base hover:text-tech-cyan hover:underline transition-colors block">
                                {taskerNames[req.taskerId] || '…'}
                              </Link>
                              <div className="text-tech-text-muted text-xs mt-0.5">
                                Sent {formatDate(req.createdAt)}
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-3 self-start sm:self-center">
                            {req.status === 'pending' && (
                              <>
                                <button
                                  type="button"
                                  onClick={async () => {
                                    try {
                                      await rejectConnectionRequest(req.id);
                                    } catch (err) {
                                      alert(
                                        err instanceof Error ? err.message : 'Could not decline request.'
                                      );
                                    }
                                  }}
                                  className="px-4 py-2 border border-tech-border rounded-lg text-tech-text-muted hover:text-tech-text hover:bg-tech-gray transition-colors"
                                >
                                  Decline
                                </button>
                                <button
                                  type="button"
                                  onClick={async () => {
                                    try {
                                      await acceptConnectionRequest(req.id);
                                    } catch (err) {
                                      alert(
                                        err instanceof Error ? err.message : 'Could not accept request.'
                                      );
                                    }
                                  }}
                                  className="px-4 py-2 rounded-lg font-bold text-black border border-transparent hover:opacity-90 transition-opacity"
                                  style={{ background: 'linear-gradient(135deg, #00d9ff 0%, #0066ff 100%)' }}
                                >
                                  Accept & Chat
                                </button>
                              </>
                            )}
                            {req.status === 'rejected' && (
                              <span className="px-3 py-1 bg-tech-gray/50 border border-tech-border rounded text-tech-text-muted text-xs">
                                Declined
                              </span>
                            )}
                            {req.status === 'accepted' && (
                              <button
                                onClick={() => setActiveChatReqId(activeChatReqId === req.id ? null : req.id)}
                                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors border ${
                                  activeChatReqId === req.id 
                                    ? 'bg-tech-cyan/20 border-tech-cyan text-tech-cyan' 
                                    : 'bg-tech-accent/10 border-tech-accent/30 text-tech-accent hover:bg-tech-accent/20'
                                }`}
                              >
                                {activeChatReqId === req.id ? 'Close Chat' : 'Open Chat'}
                                <ChevronRight className={`w-4 h-4 transition-transform ${activeChatReqId === req.id ? 'rotate-90' : ''}`} />
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Optional Intro Message from Tasker */}
                        {req.message && req.status === 'pending' && (
                          <div className="p-4 bg-tech-dark/40 italic text-tech-text-muted text-sm border-b border-tech-border/30">
                            "{req.message}"
                          </div>
                        )}

                        {/* Render inline chat if ACTIVE */}
                        {req.status === 'accepted' && activeChatReqId === req.id && (
                          <div className="h-[600px] md:h-[700px] border-t-2 border-tech-cyan/30 shadow-inner bg-black/20">
                            <TaskChat
                              connectionId={req.id}
                              taskId={task.id}
                              otherParticipantName={taskerNames[req.taskerId]}
                            />
                          </div>
                        )}
                        
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            {task.visibility !== 'public' && !isOwner && (
              <p className="text-sm text-tech-text-muted mb-8">
                Connections are only open on public tasks. This task is private.
              </p>
            )}
            </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskHatchDetail;
